import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import { algoliasearch } from 'algoliasearch';

import { type Schema } from '../../../../amplify/data/resource';

/** One search result, whatever entity it came from. */
export interface SearchHit {
  objectID: string;
  type: 'project' | 'task' | 'todo' | 'client';
  /** Main line. */
  title: string;
  /** Secondary line: the record's context (client, phase, breadcrumb…). */
  subtitle: string | null;
  /** Where clicking the hit should go. */
  route: any[];
  queryParams?: Record<string, string>;
}

export interface SearchResults {
  projects: SearchHit[];
  tasks: SearchHit[];
  todos: SearchHit[];
  clients: SearchHit[];
  total: number;
}

const EMPTY: SearchResults = { projects: [], tasks: [], todos: [], clients: [], total: 0 };

interface SearchCredentials {
  appId: string;
  key: string;
  indices: Record<'projects' | 'tasks' | 'todos' | 'clients', string>;
  validUntil: number;
}

@Injectable({ providedIn: 'root' })
export class SearchService {
  private _client?: ReturnType<typeof generateClient<Schema>>;

  private get client() {
    if (!this._client) this._client = generateClient<Schema>();
    return this._client;
  }

  private credentials?: SearchCredentials;
  private algolia?: ReturnType<typeof algoliasearch>;
  private pending?: Promise<void>;

  /** Non-null when search is unavailable, so the UI can say so instead of looking empty. */
  error: string | null = null;

  /**
   * Fetches a tenant-scoped key and builds the client.
   *
   * The key is short-lived and this is a desktop app that stays open for days, so
   * `search()` re-authenticates on expiry rather than assuming one key per session.
   * Concurrent callers share one in-flight request.
   */
  private async authenticate(): Promise<void> {
    if (this.pending) return this.pending;

    this.pending = (async () => {
      try {
        const { data, errors } = await this.client.queries.algoliaSearchKey({});
        if (errors?.length) throw new Error(errors[0].message);

        const creds = JSON.parse(data ?? '{}') as SearchCredentials;
        if (!creds.appId || !creds.key) throw new Error('Search key response was empty.');

        this.credentials = creds;
        this.algolia = algoliasearch(creds.appId, creds.key);
        this.error = null;
      } catch (e) {
        this.credentials = undefined;
        this.algolia = undefined;
        this.error = 'Search is unavailable.';
        console.error('[Search] Could not obtain a search key', e);
      } finally {
        this.pending = undefined;
      }
    })();

    return this.pending;
  }

  /** True when the signed key is within a minute of expiring. */
  private get expired(): boolean {
    if (!this.credentials) return true;
    return Date.now() / 1000 > this.credentials.validUntil - 60;
  }

  /**
   * Searches all four indices in one request. Returns empty results rather than
   * throwing: a search box must never break the page it sits in.
   */
  async search(query: string, hitsPerIndex = 5): Promise<SearchResults> {
    const q = query.trim();
    if (!q) return EMPTY;

    if (this.expired) await this.authenticate();
    if (!this.algolia || !this.credentials) return EMPTY;

    const { indices } = this.credentials;
    const order = ['projects', 'tasks', 'todos', 'clients'] as const;

    try {
      const { results } = await this.algolia.search({
        requests: order.map(key => ({
          indexName: indices[key],
          query: q,
          hitsPerPage: hitsPerIndex,
        })),
      });

      const out: SearchResults = { ...EMPTY, projects: [], tasks: [], todos: [], clients: [], total: 0 };
      order.forEach((key, i) => {
        const hits = ((results[i] as any)?.hits ?? []).map((h: any) => this.toHit(key, h));
        out[key] = hits;
        out.total += hits.length;
      });
      return out;
    } catch (e: any) {
      // A rejected key means it expired between the check and the call; retry once.
      if (e?.status === 403 && this.credentials) {
        this.credentials = undefined;
        await this.authenticate();
        if (this.algolia) return this.search(query, hitsPerIndex);
      }
      console.error('[Search] Query failed', e);
      this.error = 'Search is unavailable.';
      return EMPTY;
    }
  }

  /**
   * Shapes a raw record into a displayable hit.
   *
   * Only ids and display strings are read. Detail views re-fetch through AppSync,
   * where the real authorisation rules apply — so a stale index can leak a title,
   * never a record.
   */
  private toHit(kind: 'projects' | 'tasks' | 'todos' | 'clients', h: any): SearchHit {
    switch (kind) {
      case 'projects':
        return {
          objectID: h.objectID,
          type: 'project',
          title: h.projectName,
          subtitle: [h.clientName, h.tenantName].filter(Boolean).join(' · ') || null,
          route: ['/projects', h.objectID],
        };
      case 'tasks':
        return {
          objectID: h.objectID,
          type: 'task',
          title: h.nameTask,
          subtitle: [h.projectName, h.stageName].filter(Boolean).join(' › ') || null,
          route: h.projectId ? ['/projects', h.projectId, 'tasks', h.objectID] : ['/projects'],
        };
      case 'todos':
        return {
          objectID: h.objectID,
          type: 'todo',
          title: h.name,
          subtitle: h.breadcrumb || null,
          route: ['/tasks'],
          queryParams: { todoId: h.objectID },
        };
      case 'clients':
        return {
          objectID: h.objectID,
          type: 'client',
          title: h.name,
          subtitle: [h.email, ...(h.tenantNames ?? [])].filter(Boolean).join(' · ') || null,
          route: ['/crm'],
          queryParams: { clientId: h.objectID },
        };
    }
  }
}
